import { BaseDomainModel } from '../models/base/base-domain-model';
import { MenuAPI } from '../api/menu-api';
import { TemplateAPI } from '../api/template-api';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { exists } from '../functions/exists';
import { PrintCardMenu } from '../models/menu/print-card/print-card-menu';
import { VariantAssetService } from '../modules/services/variant-asset-service';
import { LabelDomainModel } from './label-domain-model';
import { LocationDomainModel } from './location-domain-model';
import { CompanyDomainModel } from './company-domain-model';
import { ShelfTalkerMenu } from '../models/menu/shelf-talkers/shelf-talker-menu';
import { LaysOutCardsOnToPaperMenu } from '../models/menu/print-card/lays-out-cards-on-to-paper-menu';
import { SectionLayoutType } from '../models/enum/dto/section-layout-type.enum';
import { StiiizyPrintCardMenu } from '../models/menu/print-card/stiiizy-print-card-menu';
import { FikaEdiblesCardMenu } from '../models/menu/print-card/fika-edibles-card-menu';
import { StiiizyStrainPrintCardMenu } from '../models/menu/print-card/stiiizy-strain-print-card-menu';
import { NumberUtils } from '../utils/number.utils';
import { DistinctUtils } from '../utils/distinct.utils';
import type { CardStack } from '../models/menu/section/card-stacks/card-stack';
import type { CompanyConfiguration } from '../models/company/dto/company-configuration';
import type { LocationConfiguration } from '../models/company/dto/location-configuration';
import type { LocationPriceStream } from '../models/enum/shared/location-price-stream';
import { MasterKushStrainPrintCardMenu } from '../models/menu/print-card/master-kush-strain-print-card-menu';

type VariantCardCountMap = { [key: string]: number };

@Injectable({ providedIn: 'root' })
export class PrintCardDomainModel extends BaseDomainModel {
  constructor(
    private companyDomainModel: CompanyDomainModel,
    private labelDomainModel: LabelDomainModel,
    private locationDomainModel: LocationDomainModel,
    private menuAPI: MenuAPI,
    private templateAPI: TemplateAPI,
    private variantAssetService: VariantAssetService
  ) {
    super();
  }

  public readonly variantMetadataLoaded$ = this.variantAssetService.variantMetadataLoaded$;

  private readonly _apiRespondedWithMenu = new BehaviorSubject<boolean>(false);
  public readonly apiRespondedWithMenu$ = this._apiRespondedWithMenu as Observable<boolean>;

  private readonly _companyId = new BehaviorSubject<number | null>(null);
  public readonly companyId$ = this._companyId.pipe(distinctUntilChanged());
  public readonly companyConfig$ = this.companyDomainModel.companyConfig$;

  private readonly _locationId = new BehaviorSubject<number | null>(null);
  public readonly locationId$ = this._locationId.pipe(distinctUntilChanged());
  connectToLocationId = (id: number) => this._locationId.next(id);

  public readonly locationConfig$ = this.locationDomainModel.locationConfig$;

  private readonly _printCardMenuId = new BehaviorSubject<string | null>(null);
  public readonly printCardMenuId$ = this._printCardMenuId as Observable<string | null>;
  connectToPrintCardMenuId = (id: string) => this._printCardMenuId.next(id);

  private readonly _partialPrintCardMenuId = new BehaviorSubject<string | null>(null);
  public readonly partialPrintCardMenuId$ = this._partialPrintCardMenuId as Observable<string | null>;
  connectToPartialPrintCardMenuId = (id: string) => this._partialPrintCardMenuId.next(id);

  private readonly _partialShelfTalkerMenuId = new BehaviorSubject<string | null>(null);
  public readonly partialShelfTalkerMenuId$ = this._partialShelfTalkerMenuId as Observable<string | null>;
  connectToPartialShelfTalkerMenuId = (id: string) => this._partialShelfTalkerMenuId.next(id);

  private readonly _printCardMenuTemplateId = new BehaviorSubject<string | null>(null);
  public readonly printCardMenuTemplateId$ = this._printCardMenuTemplateId as Observable<string | null>;
  connectToPrintCardMenuTemplateId = (id: string) => this._printCardMenuTemplateId.next(id);

  private readonly _partialPrintCardMenuTemplateId = new BehaviorSubject<string | null>(null);
  public readonly partialPrintCardMenuTemplateId$ = this._partialPrintCardMenuTemplateId as Observable<string | null>;
  connectToPartialPrintCardMenuTemplateId = (id: string) => this._partialPrintCardMenuTemplateId.next(id);

  private readonly _partialShelfTalkerTemplateId = new BehaviorSubject<string | null>(null);
  public readonly partialShelfTalkerTemplateId$ = this._partialShelfTalkerTemplateId as Observable<string | null>;
  connectToPartialShelfTalkerTemplateId = (id: string) => this._partialShelfTalkerTemplateId.next(id);

  private readonly _overrideVariantIds = new BehaviorSubject<string[] | null>(null);
  public readonly overrideVariantIds$ = this._overrideVariantIds as Observable<string[] | null>;
  connectToOverrideVariantIds = (ids: string[]) => {
    this._overrideVariantIds.next(ids);
  };

  private readonly _overrideSectionIds = new BehaviorSubject<string[] | null>(null);
  public readonly overrideSectionIds$ = this._overrideSectionIds as Observable<string[] | null>;
  connectToOverrideSectionIds = (ids: string[]) => {
    this._overrideSectionIds.next(ids);
    // only fetch variant assets for specified sections
    this.variantAssetService.connectToOverrideSectionIds(ids);
  };

  /**
   * The name overrideSiblingVariantIds is a bit deceptive, override sibling variant ids contain sibling ids for
   * ALL relevant overrideVariantIds.
   *
   * Sibling variant ids are only needed for rendering smart prints, because smart prints narrow down the list of
   * variants to variants that have data changes. We need to show all siblings alongside the changed variant
   * for multi-variant cards.
   */
  private readonly _overrideSiblingVariantIds = new BehaviorSubject<string[] | null>(null);
  public readonly overrideSiblingVariantIds$ = this._overrideSiblingVariantIds as Observable<string[] | null>;
  connectToOverrideSiblingVariantIds = (ids: string[]) => this._overrideSiblingVariantIds.next(ids);

  private readonly overrideVariantIdsWithSiblingIds$ = combineLatest([
    this.overrideVariantIds$.notNull$(),
    this.overrideSiblingVariantIds$.notNull$()
  ]).pipe(
    map(([variantIds, siblingVariantIds]) => [...(variantIds || []), ...(siblingVariantIds || [])].unique()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly _variantCardCountMap = new BehaviorSubject<VariantCardCountMap | null>(null);
  public readonly variantCardCountMap$ = this._variantCardCountMap as Observable<VariantCardCountMap | null>;
  connectToVariantCardCountMap = (x: VariantCardCountMap | null) => this._variantCardCountMap.next(x);

  private _printCardMenuFromAPI = new BehaviorSubject<PrintCardMenu | ShelfTalkerMenu | null>(null);
  private readonly printCardMenuFromAPI$ = this._printCardMenuFromAPI.pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly laysOutCardsOnToPaperMenu$: Observable<LaysOutCardsOnToPaperMenu> = combineLatest([
    this.printCardMenuFromAPI$,
    this.companyConfig$,
    this.locationConfig$
  ]).pipe(
    map(([menu, companyConfig, locationConfig]) => {
      if (exists(menu) && exists(companyConfig) && exists(locationConfig)) {
        menu.setImplicitProperties(companyConfig, locationConfig);
        return menu;
      }
      return null;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly shelfTalkerMenu$: Observable<ShelfTalkerMenu | null> = this.laysOutCardsOnToPaperMenu$.pipe(
    filter((menu: LaysOutCardsOnToPaperMenu): menu is ShelfTalkerMenu => menu instanceof ShelfTalkerMenu),
    startWith(null),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly adobeFontFamilyCssUrl$ = this.laysOutCardsOnToPaperMenu$.pipe(
    map(menu => menu?.getAdobeFontFamilyCssUrl() ?? null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly printCardMenu$: Observable<PrintCardMenu | null> = this.laysOutCardsOnToPaperMenu$.pipe(
    filter((menu: LaysOutCardsOnToPaperMenu): menu is PrintCardMenu => menu instanceof PrintCardMenu),
    startWith(null),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly layoutInstructions$ = this.laysOutCardsOnToPaperMenu$.pipe(
    map(laysOutCardsOnToPaperMenu => laysOutCardsOnToPaperMenu?.getLayoutInstructions()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly cardStacks$ = this.printCardMenu$.pipe(
    map(cardStackMenu => cardStackMenu?.sections ?? null),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly shelfTalkerCards$ = this.shelfTalkerMenu$.pipe(
    map(shelfTalkerMenu => shelfTalkerMenu?.getShelfTalkerCards() ?? []),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly visibleOverrideVariantIdsWithVisibleSiblingIds$ = combineLatest([
    this.cardStacks$.notNull$(),
    this.printCardMenu$.notNull$(),
    this.overrideVariantIds$.notNull$(),
    this.overrideSiblingVariantIds$.notNull$(),
    this.companyConfig$.notNull$(),
    this.locationConfig$.notNull$()
  ]).pipe(
    map(([stacks, menu, forcedVarIdPool, allVariantsSiblingIds, compConfig, locConfig]) => {
      return stacks?.flatMap(stack => {
        return this.consolidateForcedVariantIdPoolWithSiblingVariantIds(
          stack,
          menu,
          forcedVarIdPool,
          allVariantsSiblingIds,
          compConfig,
          locConfig
        );
      });
    }),
    distinctUntilChanged(DistinctUtils.distinctStrings),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /* *************************** Local Threads of Execution *************************** */

  private sendVisibleVariantIdsToVariantAssetService =
    this.visibleOverrideVariantIdsWithVisibleSiblingIds$.subscribeWhileAlive({
      owner: this,
      next: ids => {
        if (exists(ids)) {
          this.variantAssetService.connectToOverrideVariantIds(ids);
        }
      }
    });

  private fetchCompanyConfig = this.companyId$
    .notNull$()
    .pipe(switchMap(companyId => this.companyDomainModel.getCompanyConfig$(companyId)))
    .subscribeWhileAlive({ owner: this });

  private fetchLocationConfig = combineLatest([this.companyId$.notNull$(), this.locationId$.notNull$()])
    .pipe(
      switchMap(([companyId, locationId]) => this.locationDomainModel.getLocationConfiguration$(companyId, locationId))
    )
    .subscribeWhileAlive({ owner: this });

  private fetchPrintCardMenuFromAPI = combineLatest([this.locationId$.notNull$(), this.printCardMenuId$.notNull$()])
    .pipe(switchMap(([locationId, printCardMenuId]) => this.getPrintCardMenu$(locationId, printCardMenuId)))
    .subscribeWhileAlive({ owner: this });

  private fetchPartialPrintCardMenuFromAPI = combineLatest([
    this.locationId$.notNull$(),
    this.partialPrintCardMenuId$.notNull$(),
    this.overrideVariantIdsWithSiblingIds$.notNull$()
  ])
    .pipe(
      switchMap(([locationId, printCardMenuId, variantIdsWithSiblingIds]) => {
        return this.getPartialPrintCardMenu$(locationId, printCardMenuId, variantIdsWithSiblingIds);
      })
    )
    .subscribeWhileAlive({ owner: this });

  private fetchPartialShelfTalkerMenuFromAPI = combineLatest([
    this.locationId$.notNull$(),
    this.partialShelfTalkerMenuId$.notNull$(),
    this.overrideSectionIds$.notNull$()
  ])
    .pipe(
      switchMap(([locationId, printCardMenuId, sectionIds]) => {
        return this.getPartialShelfTalkerMenu$(locationId, printCardMenuId, sectionIds);
      })
    )
    .subscribeWhileAlive({ owner: this });

  private fetchPrintCardMenuTemplateFromAPI = combineLatest([
    this.locationId$.notNull$(),
    this.printCardMenuTemplateId$.notNull$()
  ])
    .pipe(switchMap(([locationId, menuTemplateId]) => this.getPrintCardMenuTemplate$(locationId, menuTemplateId)))
    .subscribeWhileAlive({ owner: this });

  private fetchPartialPrintCardMenuTemplateFromAPI = combineLatest([
    this.locationId$.notNull$(),
    this.partialPrintCardMenuTemplateId$.notNull$(),
    this.overrideVariantIdsWithSiblingIds$.notNull$()
  ])
    .pipe(
      switchMap(([locationId, menuTemplateId, variantIdsWithSiblingIds]) => {
        return this.getPrintCardPartialMenuTemplate$(locationId, menuTemplateId, variantIdsWithSiblingIds);
      })
    )
    .subscribeWhileAlive({ owner: this });

  private fetchPartialShelfTalkerMenuTemplateFromAPI = combineLatest([
    this.locationId$.notNull$(),
    this.partialShelfTalkerTemplateId$.notNull$(),
    this.overrideSectionIds$.notNull$()
  ])
    .pipe(
      switchMap(([locationId, menuTemplateId, sectionIds]) => {
        return this.getShelfTalkerPartialMenuTemplate$(locationId, menuTemplateId, sectionIds);
      })
    )
    .subscribeWhileAlive({ owner: this });

  /* ********************************************************************************** */

  private consolidateForcedVariantIdPoolWithSiblingVariantIds = (
    stack: CardStack,
    menu: PrintCardMenu,
    forcedVariantIdPool: string[] | null,
    allVariantsSiblingIds: string[] | null,
    compConfig: CompanyConfiguration,
    locConfig: LocationConfiguration
  ): string[] => {
    const layoutType = menu?.sections?.firstOrNull()?.layoutType;
    switch (layoutType) {
      case SectionLayoutType.Grid: {
        // Grid menus can consolidate all variantIds since we will re-print any cards that are affected by grid changes,
        // however, we want to remove any discontinued variant ids from the list, so that unexpected behavior doesn't
        // occur from hidden discontinued variants.
        return this.getConsolidatedActiveVariantIdsFrom(stack, forcedVariantIdPool, allVariantsSiblingIds);
      }
      case SectionLayoutType.ChildVariantList: {
        switch (true) {
          // Master Kush cards group sibling variants onto the same card as "strains", so we need to show all siblings
          case menu instanceof MasterKushStrainPrintCardMenu:
          // Stiiizy cards group sibling variants onto the same card as "flavors", so we need to show all siblings
          case menu instanceof StiiizyPrintCardMenu: {
            return this.getConsolidatedActiveVariantIdsFrom(stack, forcedVariantIdPool, allVariantsSiblingIds);
          }
          case menu instanceof FikaEdiblesCardMenu:
          case menu instanceof StiiizyStrainPrintCardMenu: {
            const tId = menu?.theme;
            const lId = locConfig?.locationId;
            const cId = compConfig?.companyId;
            const pStream = locConfig?.priceFormat;
            const hideSale = menu?.menuOptions?.hideSale;
            const activeForcedVariantIdPool = this.getActiveVariantIdsFrom(stack, forcedVariantIdPool);
            const activeVariantsSiblingIds = this.getActiveVariantIdsFrom(stack, allVariantsSiblingIds);
            return activeForcedVariantIdPool
              ?.flatMap(variantId => {
                const getIdWithSiblings = this.getVariantIdWithAllSiblingVariantIdsWithSamePrice;
                return getIdWithSiblings(stack, variantId, activeVariantsSiblingIds, tId, lId, cId, pStream, hideSale);
              })
              ?.unique();
          }
          default: {
            return this.getActiveVariantIdsFrom(stack, forcedVariantIdPool);
          }
        }
      }
      default: {
        // single variant cards don't show siblings, so we can just return the forcedVariantIdPool
        return this.getActiveVariantIdsFrom(stack, forcedVariantIdPool);
      }
    }
  };

  /**
   * "Active" is referring to non-discontinued variants (!variant.isDiscontinued).
   */
  private getActiveVariantIdsFrom = (stack: CardStack, variantIdToSearchThrough: string[]): string[] => {
    return (
      variantIdToSearchThrough?.filter(variantId => {
        const variant = stack?.variants?.find(v => v?.id === variantId);
        return !variant?.isDiscontinued;
      }) ?? []
    );
  };

  /**
   * "Active" is referring to non-discontinued variants (!variant.isDiscontinued).
   */
  private getConsolidatedActiveVariantIdsFrom = (
    stack: CardStack,
    forcedVariantIdPool: string[] | undefined,
    allVariantsSiblingIds: string[] | undefined
  ): string[] => {
    const consolidatedVariantIds = [...(forcedVariantIdPool ?? []), ...(allVariantsSiblingIds ?? [])]?.unique();
    return this.getActiveVariantIdsFrom(stack, consolidatedVariantIds);
  };

  private getVariantIdWithAllSiblingVariantIdsWithSamePrice = (
    stack: CardStack,
    variantId: string,
    allVariantsSiblingIds: string[],
    themeId: string,
    locId: number,
    compId: number,
    priceStream: LocationPriceStream,
    hideSale: boolean
  ): string[] => {
    const enabledSiblingIds = allVariantsSiblingIds?.filter(id => stack?.enabledVariantIds?.includes(id));
    const product = stack?.products?.find(p => p?.variants?.map(v => v?.id)?.includes(variantId));
    const variant = product?.variants?.find(v => v?.id === variantId);
    const variantPrice = variant?.getVisiblePrice(themeId, locId, compId, priceStream, hideSale);
    const siblingVariants = product?.variants?.filter(v => enabledSiblingIds?.includes(v?.id));
    const siblingVariantsWithSamePrice = siblingVariants?.filter(v => {
      const price = v?.getVisiblePrice(themeId, locId, compId, priceStream, hideSale);
      return NumberUtils.floatNumbersEqual(price, variantPrice);
    });
    return [variantId, ...(siblingVariantsWithSamePrice?.map(v => v?.id) ?? [])];
  };

  public getPrintCardMenu$(locationId: number, printCardMenuId: string): Observable<PrintCardMenu> {
    return this.menuAPI.getMenu$(locationId, printCardMenuId).pipe(
      concatMap(menu => this.labelDomainModel.getLabels$(locationId, menu?.companyId).pipe(map(() => menu))),
      tap((menu: PrintCardMenu) => this.menuWasReturnedFromApi(menu))
    );
  }

  public getPartialPrintCardMenu$(
    locationId: number,
    printCardMenuId: string,
    variantIds: string[]
  ): Observable<PrintCardMenu> {
    return this.menuAPI.getPartialMenu$(locationId, printCardMenuId, variantIds).pipe(
      concatMap(menu => this.labelDomainModel.getLabels$(locationId, menu?.companyId).pipe(map(() => menu))),
      tap((menu: PrintCardMenu) => this.menuWasReturnedFromApi(menu))
    );
  }

  public getPartialShelfTalkerMenu$(
    locationId: number,
    shelfTalkerMenuId: string,
    sectionIds: string[]
  ): Observable<PrintCardMenu> {
    return this.menuAPI.getPartialShelfTalkerMenu$(locationId, shelfTalkerMenuId, sectionIds).pipe(
      concatMap(menu => this.labelDomainModel.getLabels$(locationId, menu?.companyId).pipe(map(() => menu))),
      tap((menu: PrintCardMenu) => this.menuWasReturnedFromApi(menu))
    );
  }

  public getPrintCardMenuTemplate$(locationId: number, printCardMenuTemplateId: string): Observable<PrintCardMenu> {
    return this.templateAPI.getMenuTemplateForLocation$(locationId, printCardMenuTemplateId).pipe(
      concatMap(menu => this.labelDomainModel.getLabels$(locationId, menu?.companyId).pipe(map(() => menu))),
      tap((menu: PrintCardMenu) => this.menuWasReturnedFromApi(menu))
    );
  }

  public getPrintCardPartialMenuTemplate$(
    locationId: number,
    printCardMenuTemplateId: string,
    variantIds: string[]
  ): Observable<PrintCardMenu> {
    return this.templateAPI.getPartialMenuTemplate$(locationId, printCardMenuTemplateId, variantIds).pipe(
      concatMap(menu => this.labelDomainModel.getLabels$(locationId, menu?.companyId).pipe(map(() => menu))),
      tap((menu: PrintCardMenu) => this.menuWasReturnedFromApi(menu))
    );
  }

  public getShelfTalkerPartialMenuTemplate$(
    locationId: number,
    printCardMenuTemplateId: string,
    sectionIds: string[]
  ): Observable<PrintCardMenu> {
    return this.templateAPI.getPartialShelfTalkerTemplate$(locationId, printCardMenuTemplateId, sectionIds).pipe(
      concatMap(menu => this.labelDomainModel.getLabels$(locationId, menu?.companyId).pipe(map(() => menu))),
      tap((menu: PrintCardMenu) => this.menuWasReturnedFromApi(menu))
    );
  }

  private menuWasReturnedFromApi(menu: PrintCardMenu): void {
    this._companyId.next(menu?.companyId ?? null);
    this.variantAssetService.fetchVariantAssetsForMenusAssignedToDisplay([menu]);
    this._printCardMenuFromAPI.next(menu);
    this._apiRespondedWithMenu.next(true);
  }
}
